import { get } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';

import moment from 'moment';
import bind from '@reverbdotcom/commons/src/bind';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCStarRating, RCAlertBox } from '@reverbdotcom/cadence/components';
import APIRequest from './api_request';
import URLHelpers from './shared/url_helpers';
import {
  core_apimessages_CSPReview as ProductReview,
  VoteProductReview,
} from '../schemas/rql';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { connect } from '@reverbdotcom/commons/src/connect';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';

const TRUNCATION_LIMIT = 300;

interface ExternalProps {
  review: ProductReview;
  truncate?: boolean;
  cspId: string;
  trackingDisplayStyle: 'product' | 'csp';
}

interface MutationProps {
  voteProductReview: MutationFn<VoteProductReview.Mutation, VoteProductReview.Variables>;
}

type Props = ExternalProps & IUserContext & MutationProps;

interface IState {
  isReviewBlocked: boolean;
  didIVote: boolean;
  voteCount: number;
}

export class CSPReview extends React.Component<Props, IState> {
  state = {
    isReviewBlocked: false,
    didIVote: this.props.review.didIVote,
    voteCount: this.props.review.voteCount,
  };

  getAvatarSrc() {
    const avatar = get(this.props.review, 'reviewer.links.avatar.href');
    if (!avatar) return null;

    return avatar;
  }

  @bind
  handleBlockReview() {
    const url = URLHelpers.adminBlockReview(this.props.review.id);

    APIRequest.V3.put(url).then(() => {
      this.setState({ isReviewBlocked: true });
    });
  }

  @bind
  handleVote() {
    this.props.voteProductReview().then(() => {
      this.setState({
        didIVote: true,
        voteCount: this.state.voteCount + 1,
      });

      trackEvent({
        eventName: MParticleEventName.ProductReviewAction,
        productReviewInteraction: ProductReviewInteraction.VoteReview,
        displayStyle: this.props.trackingDisplayStyle,
        cspId: this.props.cspId,
      });
    });
  }

  renderBlockReviewBtn() {
    if (!this.props.user.isAdmin || this.state.isReviewBlocked) { return null; }

    return (
      <div className="align-right">
        <button
          className="button-as-link size-70 color-red"
          onClick={this.handleBlockReview}
          data-block-button
        >
          {I18n.t('discovery.csp2.admin.blockReview')}
        </button>
      </div>
    );
  }

  renderReviewBlockedAlert() {
    if (!this.state.isReviewBlocked) { return null; }

    return (
      <div className="mtb-1">
        <RCAlertBox type="error">
          {I18n.t('discovery.csp2.admin.reviewIsBlocked')}
        </RCAlertBox>
      </div>
    );
  }

  renderVoteBtn() {
    if (this.props.user.loggedOut || this.state.didIVote) {
      return null;
    }

    return (
      <button
        className="review-item__mark-helpful__button"
        onClick={this.handleVote}
      >
        {I18n.t('discovery.cspReviewModule.helpful')}
      </button>
    );
  }

  renderUserVoted() {
    if (!this.state.didIVote) {
      return null;
    }

    return (
      <p className="review-item__marked-as-helpful">
        {I18n.t('discovery.cspReviewModule.markedHelpful')}
      </p>
    );
  }

  renderVoteCount() {
    const count = this.state.voteCount;

    if (count === 0) {
      return null;
    }

    return (
      <p className="review-item__mark-helpful__count">
        {I18n.t('discovery.cspReviewModule.foundHelpfulCount', { count })}
      </p>
    );
  }

  renderVoting() {
    if (this.props.review.isMyReview) {
      return (
        <div className="review-item__mark-helpful">
          {this.renderVoteCount()}
        </div>
      );
    }

    return (
      <div className="review-item__mark-helpful">
        {this.renderVoteBtn()}
        {this.renderUserVoted()}
        {this.renderVoteCount()}
      </div>
    );
  }

  render() {
    const { review } = this.props;

    return (
      <div
        className="review-item"
        itemProp="review"
        itemScope
        itemType="http://schema.org/Review"
      >
        <meta
          content={moment(this.props.review.createdAt, 'YYYY-MM-DD HH:mm:ss ZZ').format('YYYY-MM-DD')}
          itemProp="datePublished"
        />
        {this.renderReviewBlockedAlert()}
        <div className="review-item__user">
          <img
            className="review-item__avatar"
            src={this.getAvatarSrc()}
            alt="user avatar"
            loading="lazy"
          />
          <div itemProp="author" itemScope itemType="https://schema.org/Person">
            <span itemProp="name">
              {review.reviewer.shortname}
            </span>
          </div>
        </div>
        <div className="review-item__main">
          <h4 itemProp="name" className="cms-h4 mtb-0">
            {review.title}
          </h4>
          <div className="d-flex fx-align-center mtb-half">
            <div className="mr-half">
              <div
                itemProp="reviewRating"
                itemScope
                itemType="http://schema.org/Rating"
              >
                <meta itemProp="ratingValue" content={`${review.rating}`} />
                <meta itemProp="worstRating" content="1" />
                <meta itemProp="bestRating" content="5" />
                <RCStarRating
                  rating={review.rating / 5}
                  hideTooltip
                />
              </div>
            </div>
            {review.verified &&
              <div className="size-90 weight-bold color-orange" data-review-verified>
                {I18n.t('discovery.cspReviewModule.verifiedPurchase')}
              </div>
            }
          </div>
          <meta itemProp="reviewBody" content={review.body} />
          <SanitizedRender
            truncationLimit={this.props.truncate && TRUNCATION_LIMIT}
            html={review.body}
            htmlTag="p"
            className="review-item__body"
          />
          {this.renderVoting()}
          {this.renderBlockReviewBtn()}
        </div>
      </div>
    );
  }
}

const voteProductReview =
  withGraphql<Props, VoteProductReview.Mutation, VoteProductReview.Variables>(
    gql`
    mutation VoteProductReview($input: Input_core_apimessages_ProductReviewVoteRequest) {
      voteProductReview(input: $input) {
        _
      }
    }
  `,
    {
      name: 'voteProductReview',
      options: ownProps => ({
        variables: {
          input: { id: ownProps.review.id },
        },
      }),
    },
  );

export default connect<ExternalProps>([
  voteProductReview,
  withUserContext,
])(CSPReview);
